import React from "react";
import { useTranslation } from "react-i18next";
import {
  Identifiable,
  Project,
  Resource,
  Task,
  allocation,
  task,
  project,
} from "@planzone/dto";
import {
  useAllocateResources,
  useRetrieveResourceSubordinates,
} from "@planzone/view-models";
import { Flag, InfiniteScrollRoot } from "@common/infiniteScroll";
import { Button } from "@common/button";
import { Modal } from "@common/modal";
import { ProjectSelection } from "./projectSelection";
import { TasksSelection } from "./tasksSelection";
import { ResourceSelection } from "@common/resourceSelection";
import { Props } from "./types";
import styles from "./styles.scss";

function AssignToTask({
  cancel,
  showPersonalView,
  currentResource,
  onAllocated,
}: Props) {
  const { t } = useTranslation();
  const {
    resources,
    retrieveResourceSubordinates,
    isRequestSuccess,
    isRequestPending: isSubordinatesPending,
    canLoadMore,
    currentPage,
  } = useRetrieveResourceSubordinates(currentResource);

  React.useEffect(() => {
    !isRequestSuccess &&
      !isSubordinatesPending &&
      retrieveResourceSubordinates({
        resource: currentResource,
        page: 0,
        size: 5,
      });
  }, []);

  const [selectedResource, setSelectedResource] =
    React.useState<Identifiable<Resource> | null>(
      showPersonalView ? currentResource : null,
    );
  const [selectedProject, setSelectedProject] =
    React.useState<Identifiable<Project> | null>(null);
  const [selectedTask, setSelectedTask] =
    React.useState<Identifiable<Task> | null>(null);
  const { isRequestPending, allocateResources } = useAllocateResources(
    selectedTask || { ...task, uuid: "" },
  );

  return (
    <Modal header={t("assignToTask.title")} close={cancel}>
      <InfiniteScrollRoot canFetchOnIdle>
        <div className={styles.assignToTask}>
          <div className={styles.body}>
            {!showPersonalView && (
              <div className={styles.field}>
                <label className={styles.label}>
                  {t("assignToTask.resourceLabel")}
                </label>
                <ResourceSelection
                  resources={resources}
                  selected={selectedResource}
                  placeholder={t("assignToTask.resourcePlaceholder")}
                  onSelect={(resource) => {
                    setSelectedResource(resource);
                    setSelectedProject(null);
                    setSelectedTask(null);
                  }}
                />
                <Flag
                  name="subordinates"
                  isDisabled={isSubordinatesPending || !canLoadMore}
                  retrieveItems={() =>
                    retrieveResourceSubordinates({
                      resource: currentResource,
                      page: currentPage,
                      size: 5,
                    })
                  }
                />
              </div>
            )}
            {selectedResource && (
              <div className={styles.field}>
                <label className={styles.label}>
                  {t("assignToTask.projectLabel")}
                </label>
                <ProjectSelection
                  selected={selectedProject}
                  placeholder={t("assignToTask.projectPlaceholder")}
                  onSelect={(project) => {
                    setSelectedProject(project);
                    setSelectedTask(null);
                  }}
                />
              </div>
            )}
            {selectedProject && selectedResource && (
              <div className={styles.field}>
                <label className={styles.label}>
                  {t("assignToTask.taskLabel")}
                </label>
                <TasksSelection
                  project={selectedProject}
                  resource={selectedResource}
                  selected={selectedTask}
                  onSelect={setSelectedTask}
                />
              </div>
            )}
          </div>
          <div className={styles.footer}>
            <Button
              type="submit"
              status={isRequestPending ? "pending" : "none"}
              isDisabled={
                isRequestPending || !selectedResource || !selectedTask
              }
              press={() => {
                if (selectedTask && selectedResource && selectedProject) {
                  onAllocated?.(
                    selectedTask.uuid,
                    selectedProject.uuid,
                    selectedResource.uuid,
                  );
                  allocateResources({
                    task: selectedTask,
                    allocations: [
                      { ...allocation, resource: selectedResource },
                    ],
                    project: selectedProject,
                  });
                }
              }}
            >
              {t("assignToTask.confirm")}
            </Button>
          </div>
        </div>
      </InfiniteScrollRoot>
    </Modal>
  );
}

export { AssignToTask };
